import type {
  CheckpointMetric,
  OrderDashboardReport,
  PartnerMetric,
} from '@/api/hooks/useOrderDashboard'

/**
 * `/dashboard/orders/metrics` reports per partner: one row each in
 * `partner_metrics`, and a full set of event rows each in `checkpoint_metrics`.
 * The partner rows are already whole-window totals, so they only need ordering.
 * The event rows are summed over whichever partners are being shown, and the
 * percentages recomputed from those counts -- averaging the per-partner
 * percentages would weight a quiet partner the same as a busy one.
 */

export type CheckpointRollup = Omit<
  CheckpointMetric,
  'total' | 'percentage'
> & {
  total: number
  percentage: number | null
}

function ratio(succeeded: number, total: number): number | null {
  return total === 0 ? null : succeeded / total
}

/**
 * Partners over the whole window, healthiest first, matching the design.
 * Partners with no high-severity events at all have no rate and sort last.
 */
export function sortPartnerMetrics(
  report: OrderDashboardReport | undefined,
): PartnerMetric[] {
  return Object.values(report?.partner_metrics ?? {}).sort(
    (a, b) =>
      (b.high_events_percentage ?? Number.NEGATIVE_INFINITY) -
        (a.high_events_percentage ?? Number.NEGATIVE_INFINITY) ||
      a.tenant_name.localeCompare(b.tenant_name),
  )
}

/**
 * Events over the whole window, in the order the backend emits them (high
 * severity first). Labels are the backend's, so renaming an event is a
 * backend change rather than a translation table over here.
 *
 * `partnerName` keys into the report's per-partner breakdown, so picking a
 * partner narrows these rows the same way it narrows the issue list beside
 * them. Null sums every partner.
 */
export function rollupCheckpointMetrics(
  report: OrderDashboardReport | undefined,
  partnerName: string | null = null,
): CheckpointRollup[] {
  const byPartner = report?.checkpoint_metrics ?? {}
  const shown = partnerName
    ? [byPartner[partnerName] ?? []]
    : Object.values(byPartner)

  const byLabel = new Map<string, CheckpointRollup>()

  for (const metrics of shown) {
    for (const metric of metrics) {
      const acc = byLabel.get(metric.label) ?? {
        label: metric.label,
        severity: metric.severity,
        success: 0,
        warning: 0,
        failed: 0,
        silenced: 0,
        none: 0,
        total: 0,
        percentage: null,
      }
      acc.success += metric.success
      acc.warning += metric.warning
      acc.failed += metric.failed
      acc.silenced += metric.silenced
      acc.none += metric.none
      byLabel.set(metric.label, acc)
    }
  }

  return [...byLabel.values()].map((acc) => {
    // Silenced events ran and were muted on purpose, so they stay in the
    // denominator and count as delivered -- the same arithmetic the backend
    // does per partner. Only `none` (the saga never ran) is left out.
    const total = acc.success + acc.warning + acc.failed + acc.silenced
    return {
      ...acc,
      total,
      percentage: ratio(acc.success + acc.warning + acc.silenced, total),
    }
  })
}

export type MetricTone = 'success' | 'warning' | 'error'

/**
 * How a success rate is coloured. A checkpoint that never fired has no rate
 * and so no tone.
 */
export function percentageTone(
  percentage: number | null | undefined,
): MetricTone | null {
  if (percentage == null) return null
  if (percentage >= 0.99) return 'success'
  if (percentage >= 0.95) return 'warning'
  return 'error'
}

export const TONE_COLORS: Record<MetricTone, string> = {
  success: 'var(--theme-success)',
  warning: 'var(--theme-warning)',
  error: 'var(--theme-error)',
}
